import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Calendar, Clock, User, Mail, Phone, MapPin, MessageSquare, CheckCircle, XCircle, Trash2, Edit, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SolicitudesReserva = () => {
  const { propiedadSeleccionada } = usePropiedad();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [solicitudConfirmar, setSolicitudConfirmar] = useState(null);
  const [solicitudRechazar, setSolicitudRechazar] = useState(null);
  const [solicitudEliminar, setSolicitudEliminar] = useState(null);
  const [solicitudEditar, setSolicitudEditar] = useState(null);
  const [formEdicion, setFormEdicion] = useState({
    nombre_cliente: '',
    apellido_cliente: '',
    email_cliente: '',
    telefono_cliente: '',
    rut_cliente: '',
    direccion_cliente: '',
    fecha_check_in: '',
    fecha_check_out: '',
    cantidad_personas: 1,
    mensaje: '',
    estado: 'pendiente'
  });
  const [canales, setCanales] = useState([]);
  const [datosConfirmacion, setDatosConfirmacion] = useState({
    canal_id: '',
    monto_bruto: 0,
    comisiones: 0
  });

  useEffect(() => {
    cargarCanales();
  }, []);

  useEffect(() => {
    if (propiedadSeleccionada?.id_propiedad) {
      cargarSolicitudes();
    }
  }, [propiedadSeleccionada?.id_propiedad]);

  const cargarCanales = async () => {
    try {
      const response = await axios.get(`${API}/canales`);
      setCanales(response.data);
    } catch (error) {
      toast.error('Error al cargar canales');
      console.error(error);
    }
  };

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/solicitudes-reserva`, {
        params: { propiedad_id: propiedadSeleccionada?.id_propiedad }
      });
      setSolicitudes(response.data);
    } catch (error) {
      toast.error('Error al cargar solicitudes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    // Formatear con separador de miles con punto
    const formatted = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return formatted;
  };

  const calcularPrecioReserva = async (solicitud) => {
    try {
      const response = await axios.get(`${API}/public/propiedad/${solicitud.propiedad_id}/precios`, {
        params: {
          fecha_inicio: solicitud.fecha_check_in,
          fecha_fin: solicitud.fecha_check_out
        }
      });
      return response.data.total || 0;
    } catch (error) {
      console.error('Error al calcular precio:', error);
      return 0;
    }
  };

  const abrirModalConfirmar = async (solicitud) => {
    setSolicitudConfirmar(solicitud);
    
    // Calcular precio automáticamente
    const precioCalculado = await calcularPrecioReserva(solicitud);
    
    setDatosConfirmacion({ 
      canal_id: canales[0]?.id_canal || '', 
      monto_bruto: precioCalculado, 
      comisiones: 0 
    });
  };

  const confirmarSolicitud = async () => {
    if (!datosConfirmacion.canal_id) {
      toast.error('Debes seleccionar un canal de arriendo');
      return;
    }

    try {
      const response = await axios.post(`${API}/solicitudes-reserva/${solicitudConfirmar.id_solicitud}/confirmar`, datosConfirmacion);
      const resultado = response.data;
      
      // Mostrar mensaje según si se creó un nuevo cliente o se usó uno existente
      if (resultado.cliente_creado) {
        toast.success(`✅ Reserva confirmada y nuevo cliente creado automáticamente`);
      } else {
        toast.success(`✅ Reserva confirmada - Cliente existente actualizado`);
      }
      
      setSolicitudConfirmar(null);
      setDatosConfirmacion({ canal_id: '', monto_bruto: 0, comisiones: 0 });
      cargarSolicitudes();
    } catch (error) {
      toast.error('Error al confirmar la solicitud');
      console.error(error);
    }
  };

  const rechazarSolicitud = async () => {
    try {
      await axios.post(`${API}/solicitudes-reserva/${solicitudRechazar.id_solicitud}/rechazar`, {
        motivo: 'Rechazada desde el panel de administración'
      });
      toast.success('Solicitud rechazada');
      setSolicitudRechazar(null);
      cargarSolicitudes();
    } catch (error) {
      toast.error('Error al rechazar la solicitud');
      console.error(error);
    }
  };

  const eliminarSolicitud = async () => {
    try {
      await axios.delete(`${API}/solicitudes-reserva/${solicitudEliminar.id_solicitud}`);
      toast.success('Solicitud eliminada');
      setSolicitudEliminar(null);
      cargarSolicitudes();
    } catch (error) {
      toast.error('Error al eliminar la solicitud');
      console.error(error);
    }
  };

  const abrirEdicion = (solicitud) => {
    setFormEdicion({
      nombre_cliente: solicitud.nombre_cliente || '',
      apellido_cliente: solicitud.apellido_cliente || '',
      email_cliente: solicitud.email_cliente || '',
      telefono_cliente: solicitud.telefono_cliente || '',
      rut_cliente: solicitud.rut_cliente || '',
      direccion_cliente: solicitud.direccion_cliente || '',
      fecha_check_in: solicitud.fecha_check_in || '',
      fecha_check_out: solicitud.fecha_check_out || '',
      cantidad_personas: solicitud.cantidad_personas || solicitud.num_personas || 1,
      mensaje: solicitud.mensaje || '',
      estado: solicitud.estado || 'pendiente'
    });
    setSolicitudEditar(solicitud);
  };

  const guardarEdicion = async () => {
    try {
      await axios.put(`${API}/solicitudes-reserva/${solicitudEditar.id_solicitud}`, formEdicion);
      toast.success('Solicitud actualizada correctamente');
      setSolicitudEditar(null);
      cargarSolicitudes();
    } catch (error) {
      toast.error('Error al actualizar la solicitud');
      console.error(error);
    }
  };

  const calcularHorasRestantes = (fechaSolicitud) => {
    const ahora = new Date();
    const solicitud = new Date(fechaSolicitud);
    const diferencia = ahora - solicitud;
    const horasTranscurridas = Math.floor(diferencia / (1000 * 60 * 60));
    const horasRestantes = 72 - horasTranscurridas;
    return horasRestantes > 0 ? horasRestantes : 0;
  };

  const solicitudesFiltradas = solicitudes.filter(s => 
    s.estado === filtroEstado && 
    s.propiedad_id === propiedadSeleccionada?.id_propiedad
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-xl font-semibold text-[#0E3A53]">Cargando solicitudes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in-up">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] rounded-3xl p-8 text-white shadow-2xl">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Solicitudes de Reserva
        </h1>
        <p className="text-white/90 text-lg">Gestiona las solicitudes recibidas desde el formulario público</p>
      </div>

      {/* Filtros */}
      <div className="flex gap-4">
        <Button
          onClick={() => setFiltroEstado('pendiente')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all ${
            filtroEstado === 'pendiente'
              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Pendientes ({solicitudes.filter(s => s.estado === 'pendiente').length})
        </Button>
        <Button
          onClick={() => setFiltroEstado('confirmada')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all ${
            filtroEstado === 'confirmada'
              ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Confirmadas ({solicitudes.filter(s => s.estado === 'confirmada').length})
        </Button>
        <Button
          onClick={() => setFiltroEstado('rechazada')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all ${
            filtroEstado === 'rechazada'
              ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Rechazadas ({solicitudes.filter(s => s.estado === 'rechazada').length})
        </Button>
        <Button
          onClick={() => setFiltroEstado('expirada')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all ${
            filtroEstado === 'expirada'
              ? 'bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-lg'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Expiradas ({solicitudes.filter(s => s.estado === 'expirada').length})
        </Button>
      </div>

      {/* Lista de solicitudes */}
      {solicitudesFiltradas.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No hay solicitudes {filtroEstado}s</p>
        </div>
      ) : filtroEstado === 'confirmada' ? (
        // Vista minimalista para solicitudes confirmadas
        <div className="space-y-2">
          {solicitudesFiltradas.map((solicitud) => {
            const fechaCheckIn = new Date(solicitud.fecha_check_in);
            const fechaCheckOut = new Date(solicitud.fecha_check_out);
            const noches = Math.ceil((fechaCheckOut - fechaCheckIn) / (1000 * 60 * 60 * 24));

            return (
              <div
                key={solicitud.id_solicitud}
                className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:border-green-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-[#0E3A53] truncate">
                        {solicitud.nombre_cliente} {solicitud.apellido_cliente || ''}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {fechaCheckIn.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })} - {fechaCheckOut.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {noches} noches
                        </span>
                        {solicitud.num_personas && (
                          <span className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            {solicitud.num_personas} pers.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {solicitud.telefono_cliente && (
                      <a
                        href={`https://wa.me/${solicitud.telefono_cliente.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                        title="Enviar WhatsApp"
                      >
                        <MessageSquare className="w-5 h-5 text-green-600" />
                      </a>
                    )}
                    {solicitud.email_cliente && (
                      <a
                        href={`mailto:${solicitud.email_cliente}`}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title={solicitud.email_cliente}
                      >
                        <Mail className="w-5 h-5 text-blue-600" />
                      </a>
                    )}
                    <button
                      onClick={() => abrirEdicion(solicitud)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar solicitud"
                    >
                      <Edit className="w-5 h-5 text-blue-500" />
                    </button>
                    <button
                      onClick={() => setSolicitudEliminar(solicitud)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar solicitud"
                    >
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid gap-6">
          {solicitudesFiltradas.map((solicitud) => {
            const horasRestantes = calcularHorasRestantes(solicitud.fecha_solicitud);
            const fechaSolicitud = new Date(solicitud.fecha_solicitud);
            const fechaCheckIn = new Date(solicitud.fecha_check_in);
            const fechaCheckOut = new Date(solicitud.fecha_check_out);
            const noches = Math.ceil((fechaCheckOut - fechaCheckIn) / (1000 * 60 * 60 * 24));

            return (
              <div
                key={solicitud.id_solicitud}
                className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all ${
                  solicitud.estado === 'pendiente' && horasRestantes <= 12
                    ? 'border-red-400 animate-pulse-slow'
                    : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-[#0E3A53]">
                        {solicitud.nombre_cliente} {solicitud.apellido_cliente || ''}
                      </h3>
                      {solicitud.estado === 'pendiente' && (
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          horasRestantes <= 12 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          ⏱️ {horasRestantes}h restantes
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-semibold text-[#1AA1D8] mb-3">{solicitud.propiedad_nombre}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Calendar className="w-4 h-4 text-[#1AA1D8]" />
                        <span className="font-medium">Check-in:</span> {fechaCheckIn.toLocaleDateString('es-CL')}
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Calendar className="w-4 h-4 text-[#1AA1D8]" />
                        <span className="font-medium">Check-out:</span> {fechaCheckOut.toLocaleDateString('es-CL')}
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="w-4 h-4 text-[#1AA1D8]" />
                        <span className="font-medium">Duración:</span> {noches} noches
                      </div>
                      {solicitud.num_personas && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <User className="w-4 h-4 text-[#1AA1D8]" />
                          <span className="font-medium">Personas:</span> {solicitud.num_personas}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 mb-4">
                      {solicitud.email_cliente && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="w-4 h-4" />
                          <span>{solicitud.email_cliente}</span>
                        </div>
                      )}
                      {solicitud.telefono_cliente && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{solicitud.telefono_cliente}</span>
                        </div>
                      )}
                      {solicitud.rut_cliente && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span className="font-medium">RUT:</span> {solicitud.rut_cliente}
                        </div>
                      )}
                      {solicitud.direccion_cliente && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span>{solicitud.direccion_cliente}</span>
                        </div>
                      )}
                      {solicitud.cedula_url && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <span className="font-medium">Documento:</span>
                          <a 
                            href={`${BACKEND_URL}${solicitud.cedula_url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-cyan-600 hover:text-cyan-700 underline"
                          >
                            Ver documento
                          </a>
                        </div>
                      )}
                    </div>

                    {solicitud.mensaje && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 text-gray-500 mt-1" />
                          <div>
                            <p className="font-semibold text-gray-700 mb-1">Mensaje del cliente:</p>
                            <p className="text-gray-600 text-sm">{solicitud.mensaje}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500">
                        Solicitud recibida: {fechaSolicitud.toLocaleDateString('es-CL')} a las {fechaSolicitud.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  {solicitud.estado === 'pendiente' && (
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        onClick={() => abrirModalConfirmar(solicitud)}
                        className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 shadow-lg transition-all flex items-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Confirmar
                      </Button>
                      <Button
                        onClick={() => setSolicitudRechazar(solicitud)}
                        className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-xl hover:from-red-600 hover:to-red-700 shadow-lg transition-all flex items-center gap-2"
                      >
                        <XCircle className="w-5 h-5" />
                        Rechazar
                      </Button>
                      <Button
                        onClick={() => abrirEdicion(solicitud)}
                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-blue-700 shadow-lg transition-all flex items-center gap-2"
                      >
                        <Edit className="w-5 h-5" />
                        Editar
                      </Button>
                    </div>
                  )}

                  {solicitud.estado !== 'pendiente' && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => abrirEdicion(solicitud)}
                        className="px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-xl hover:bg-blue-200 transition-all flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Editar
                      </Button>
                      <Button
                        onClick={() => setSolicitudEliminar(solicitud)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-all flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Edición */}
      <Dialog open={!!solicitudEditar} onOpenChange={() => setSolicitudEditar(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">
              Editar Solicitud de Reserva
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={formEdicion.nombre_cliente}
                  onChange={(e) => setFormEdicion({ ...formEdicion, nombre_cliente: e.target.value })}
                  placeholder="Nombre"
                />
              </div>
              <div>
                <Label>Apellido</Label>
                <Input
                  value={formEdicion.apellido_cliente}
                  onChange={(e) => setFormEdicion({ ...formEdicion, apellido_cliente: e.target.value })}
                  placeholder="Apellido"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formEdicion.email_cliente}
                  onChange={(e) => setFormEdicion({ ...formEdicion, email_cliente: e.target.value })}
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={formEdicion.telefono_cliente}
                  onChange={(e) => setFormEdicion({ ...formEdicion, telefono_cliente: e.target.value })}
                  placeholder="+56 9 XXXX XXXX"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>RUT</Label>
                <Input
                  value={formEdicion.rut_cliente}
                  onChange={(e) => setFormEdicion({ ...formEdicion, rut_cliente: e.target.value })}
                  placeholder="12.345.678-9"
                />
              </div>
              <div>
                <Label>Cantidad de Personas</Label>
                <Input
                  type="number"
                  min="1"
                  value={formEdicion.cantidad_personas}
                  onChange={(e) => setFormEdicion({ ...formEdicion, cantidad_personas: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input
                value={formEdicion.direccion_cliente}
                onChange={(e) => setFormEdicion({ ...formEdicion, direccion_cliente: e.target.value })}
                placeholder="Dirección completa"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha Check-in</Label>
                <Input
                  type="date"
                  value={formEdicion.fecha_check_in}
                  onChange={(e) => setFormEdicion({ ...formEdicion, fecha_check_in: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha Check-out</Label>
                <Input
                  type="date"
                  value={formEdicion.fecha_check_out}
                  onChange={(e) => setFormEdicion({ ...formEdicion, fecha_check_out: e.target.value })}
                  min={formEdicion.fecha_check_in}
                />
              </div>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={formEdicion.estado} onValueChange={(value) => setFormEdicion({ ...formEdicion, estado: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="confirmada">Confirmada</SelectItem>
                  <SelectItem value="rechazada">Rechazada</SelectItem>
                  <SelectItem value="expirada">Expirada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mensaje / Notas</Label>
              <Textarea
                value={formEdicion.mensaje}
                onChange={(e) => setFormEdicion({ ...formEdicion, mensaje: e.target.value })}
                placeholder="Mensaje o notas adicionales"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setSolicitudEditar(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={guardarEdicion}
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
            >
              Guardar Cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmación */}
      {solicitudConfirmar && (
        <AlertDialog open={!!solicitudConfirmar} onOpenChange={() => setSolicitudConfirmar(null)}>
          <AlertDialogContent className="max-w-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar Reserva</AlertDialogTitle>
              <AlertDialogDescription>
                Completa los datos de la reserva para confirmar la solicitud de {solicitudConfirmar.nombre_cliente}
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Canal de Arriendo *</label>
                <select
                  value={datosConfirmacion.canal_id}
                  onChange={(e) => setDatosConfirmacion({ ...datosConfirmacion, canal_id: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[#1AA1D8]"
                  required
                >
                  <option value="">Selecciona un canal</option>
                  {canales.map(canal => (
                    <option key={canal.id_canal} value={canal.id_canal}>{canal.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monto Bruto (calculado automáticamente)
                </label>
                <input
                  type="number"
                  value={datosConfirmacion.monto_bruto}
                  onChange={(e) => setDatosConfirmacion({ ...datosConfirmacion, monto_bruto: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border-2 border-cyan-300 rounded-xl focus:outline-none focus:border-[#1AA1D8] bg-cyan-50 font-semibold"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Este monto incluye las noches de estadía y otros cobros configurados
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Comisiones</label>
                <input
                  type="number"
                  value={datosConfirmacion.comisiones}
                  onChange={(e) => setDatosConfirmacion({ ...datosConfirmacion, comisiones: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[#1AA1D8]"
                  placeholder="0"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Monto Neto:</span> ${formatCurrency(datosConfirmacion.monto_bruto - datosConfirmacion.comisiones)}
                </p>
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmarSolicitud}>
                Confirmar Reserva
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Modal de Rechazo */}
      {solicitudRechazar && (
        <AlertDialog open={!!solicitudRechazar} onOpenChange={() => setSolicitudRechazar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Rechazar solicitud?</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de que quieres rechazar la solicitud de <strong>{solicitudRechazar.nombre_cliente}</strong>? 
                Las fechas volverán a estar disponibles.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={rechazarSolicitud} className="bg-red-600 hover:bg-red-700">
                Rechazar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Modal de Eliminación */}
      {solicitudEliminar && (
        <AlertDialog open={!!solicitudEliminar} onOpenChange={() => setSolicitudEliminar(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar solicitud?</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de que quieres eliminar esta solicitud? Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={eliminarSolicitud} className="bg-red-600 hover:bg-red-700">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default SolicitudesReserva;
